import React from 'react';
import styled, { css } from 'styled-components';
import gql from 'graphql-tag';
import { Text, spaceMixin, mediaMixin } from '@zillow/constellation';
import { comma } from '@zillow/formatters';
import { getHomeStatusDisplayData } from '@zillow/home-status-display-data';
import type {
    Property,
    AbTests,
    HomeStatus,
    HomeType,
    ListingSubtype,
    ContingentListingType,
} from '../types/@zillow-types/pongo';
import { formatAddress, splitAddressLines } from '../utils/addressUtils';
import StatusIcon from './StatusIcon';

const StyledDiv = styled.div`
    height: 200px;
    position: relative;
    overflow: hidden;
    width: 100%;
`;

const StyledImage = styled.img`
    position: absolute;
    transform: translateY(-50%);
    width: 100%;
    top: 50%;
`;

const ImageTintCover = styled.div`
    position: absolute;
    background-color: rgba(25, 52, 91, 0.75);
    height: 100%;
    width: 100%;
    top: 0;
`;

const StyledHomeInformation = styled.div`
    padding: ${spaceMixin(2.5)};
    position: absolute;
    top: 0;

    ${mediaMixin(
        'md_lte',
        css`
            padding: ${spaceMixin(1.25)};
        `
    )};
`;

const OwnerDashboardLinkContainer = styled.a`
    position: absolute;
    right: 30px;
    bottom: 20px;
`;

interface HomeInfoProps {
    property: Property & {
        homeStatus: HomeStatus;
        homeType: HomeType;
        listing_sub_type: ListingSubtype;
        contingentListingType?: ContingentListingType;
    };
    abTests: AbTests;
    ownerDashboardLink: React.ReactNode;
}

const HomeInfo = ({ property, abTests = {}, ownerDashboardLink }: HomeInfoProps) => {
    const fullAddress = formatAddress(property);
    const fullAddressSplit = splitAddressLines(fullAddress);
    const homeStatus = property.homeStatus === 'OTHER' ? 'OFF MARKET' : property.homeStatus;
    const { icon } = getHomeStatusDisplayData(
        property.homeStatus,
        property.homeType,
        false,
        property.listing_sub_type,
        property.contingentListingType,
        abTests
    );
    const bedsBathsSqft = [
        {
            content: `${property.bedrooms ? property.bedrooms : '--'} bds`,
            predicate: property.bedrooms,
        },
        {
            content: `${property.bathrooms ? property.bathrooms : '--'} ba`,
            predicate: property.bathrooms,
        },
        {
            content: `${property.livingArea ? comma(property.livingArea) : '--'} sqft`,
            predicate: property.livingArea,
        },
    ]
        .filter(({ predicate }) => predicate)
        .map(({ content }) => content)
        .join(' · ');
    return (
        <StyledDiv data-testid="home-info">
            <StyledImage src={property.photos?.[0].url} />
            <ImageTintCover />
            <StyledHomeInformation>
                <StatusIcon icon={icon} />
                <Text data-testid="status" fontType="bodySmallHeading" fontColor="white">
                    {homeStatus.replace(/_/g, ' ')}
                    <br />
                </Text>
                <Text
                    data-testid="address"
                    fontType="h4"
                    fontColor="white"
                    mediaQuery={{ md: { fontType: 'h3' } }}
                >
                    {fullAddressSplit[0]}
                    <br />
                    {fullAddressSplit[1]}
                    <br />
                </Text>
                <Text data-testid="bedsBathsSqft" fontType="h6" fontColor="white">
                    {bedsBathsSqft}
                </Text>
            </StyledHomeInformation>
            {property.zpid && (
                <OwnerDashboardLinkContainer>{ownerDashboardLink}</OwnerDashboardLinkContainer>
            )}
        </StyledDiv>
    );
};

HomeInfo.fragments = {
    property: gql`
        fragment HomeInfo_property on Property {
            streetAddress
            city
            state
            zipcode
            bedrooms
            bathrooms
            livingArea
            homeStatus
            homeType
            contingentListingType
            photos(size: XL) {
                url
            }
            listing_sub_type {
                is_newHome
                is_FSBO
                is_bankOwned
                is_foreclosure
                is_forAuction
                is_comingSoon
            }
        }
    `,
};

export default HomeInfo;
